import { sanitizedIndices } from '../sanitizeCoords';

/** GraphHopper's `[from, to, value]` detail range, covering edges `from` to `to` − 1. */
export type DetailRange = (string | number)[];
export type EdgeValue = string | number | undefined;

/**
 * A segment's surfaces hold one value per edge: N points carry N − 1, value i covering point i
 * to i + 1. The planner keeps them as GraphHopper's ranges, so each edit expands them to one
 * value per edge, reworks that, and packs it back. An edge no range covers stays uncovered
 * rather than gaining a made-up value.
 */
export function edgeValues(ranges: DetailRange[] | undefined, pointCount: number): EdgeValue[] {
  const values: EdgeValue[] = new Array(Math.max(pointCount - 1, 0)).fill(undefined);
  for (const range of ranges ?? []) {
    if (!Array.isArray(range) || range.length < 3) continue;
    const from = Math.max(0, Number(range[0]));
    const to = Math.min(values.length, Number(range[1]));
    for (let i = from; i < to; i++) values[i] = range[2];
  }
  return values;
}

/** One range per run of equal values; uncovered edges stay uncovered. */
export function toRanges(values: EdgeValue[]): DetailRange[] {
  const ranges: DetailRange[] = [];
  let start = 0;
  for (let i = 1; i <= values.length; i++) {
    if (i < values.length && values[i] === values[start]) continue;
    const value = values[start];
    if (value !== undefined) ranges.push([start, i, value]);
    start = i;
  }
  return ranges;
}

export function reversedRanges(ranges: DetailRange[] | undefined, pointCount: number): DetailRange[] {
  return toRanges(edgeValues(ranges, pointCount).reverse());
}

/**
 * Ranges for a line built from `rawCoordinates` with these edge `values`, once
 * `coordsOnlyFeature` has dropped its repeated points. A repeated point only ever ends an edge
 * with no length, so that edge's value goes with it.
 */
export function rangesForLine(rawCoordinates: number[][], values: EdgeValue[]): DetailRange[] {
  return toRanges(sanitizedIndices(rawCoordinates).slice(1).map((index) => values[index - 1]));
}
